import { MainArea, Render, SmartLink, buildRoute, fullClassName, toggledClass } from "@techsharegov/react-ui";
import { SUBROUTES_MAP } from "Navigation/SubRouteMaps";
import { NavConfigItem, Routes, useSubNavigation, useSubRoutes } from "Navigation";
import React from "react";
import { SecondaryHeader } from "./PageHeaders";

export type AdminSubHeaderMainLayoutProps = {
  title: string;
  subRoute?: string;
  useNestedTabs?: boolean;
};

export const AdminSubHeaderMainLayout: React.FC<
  AdminSubHeaderMainLayoutProps
> = ({ title, subRoute = '', children, useNestedTabs }) => {
  return (
    <TabbedHeaderLayout
      subRoute={subRoute}
      header={
        <SecondaryHeader
          title={title}
          parentRoute={subRoute}
          largeFont={useNestedTabs}
        />
      }
      useNestedSubRouteTabs={useNestedTabs}
    >
      {children}
    </TabbedHeaderLayout>
  );
};

export type TabbedHeaderLayoutProps = {
  subRoute?: string;
  useNestedSubRouteTabs?: boolean;
  header: any;
};

export const TabbedHeaderLayout: React.FC<TabbedHeaderLayoutProps> = ({
  subRoute = '',
  children,
  header,
  useNestedSubRouteTabs,
}) => {
  const mapSubRoutes = useSubRoutes();
  const withNoSubroutes = subRoute.length === 0;

  return (
    <div
      className={fullClassName(
        'admin-sub-header-layout',
        toggledClass('with-no-subroutes', withNoSubroutes),
        toggledClass('with-nested-subroutes', useNestedSubRouteTabs)
      )}
    >
      {React.cloneElement(header as React.ReactElement<any>, {
        className: 'admin-header',
      })}
      <Render condition={useNestedSubRouteTabs}>
        <TabbedHeaderNestedTabBar subRoute={subRoute} />
      </Render>
      <MainArea className="admin-main-content">
        {withNoSubroutes ? children : mapSubRoutes(SUBROUTES_MAP[subRoute])}
      </MainArea>
    </div>
  );
};

export type TabbedHeaderNestedTabBarProps = {
  subRoute: string;
};

export const TabbedHeaderNestedTabBar = ({
  subRoute,
}: TabbedHeaderNestedTabBarProps) => {
  const navConfigItems = useSubNavigation(subRoute);
  const activeItem = navConfigItems.find((item) => item.active);
  const activeRoute = buildRoute(subRoute, activeItem?.route);

  return (
    <>
      {activeItem ? (
        <div className="admin-nested-tabs px-large">
          <TabbedHeaderNestedTabBarContents activeRoute={activeRoute} />
        </div>
      ) : null}
    </>
  );
};

export type TabbedHeaderNestedTabBarContentsProps = {
  activeRoute: string;
};

export const TabbedHeaderNestedTabBarContents = ({
  activeRoute,
}: TabbedHeaderNestedTabBarContentsProps) => {
  const subNavConfigItems = useSubNavigation(activeRoute);

  return (
    <>
      {subNavConfigItems.map((item) => (
        <TabbedHeaderNestedTab
          key={item.fullRoute}
          parentRoute={activeRoute}
          {...item}
        />
      ))}
    </>
  );
};


export type TabbedHeaderNestedTabProps = NavConfigItem & {
  parentRoute: string;
};

export const TabbedHeaderNestedTab = ({
  active,
  label,
  route,
  parentRoute,
}: TabbedHeaderNestedTabProps) => {
  return (
    <SmartLink to={buildRoute(parentRoute, route) || Routes.Unauthorized}>
      <div
        className={fullClassName(
          'nested-nav-pill',
          toggledClass('active', active)
        )}
      >
        {label}
        <div className="active-nav-underline" />
      </div>
    </SmartLink>
  );
};
